'use client';

import { useCallback, useState } from 'react';

/**
 * Tracks a boolean "busy" flag around async calls, removing the repetitive
 * setFlag(true) / try / finally setFlag(false) boilerplate from API hooks.
 *
 * Returns [busy, run]. `run` executes an async function with the flag flipped
 * for its duration and returns its result. Create one useBusy per flag you
 * want to expose (isLoading, isCreating, ...) so per-operation loading
 * granularity is preserved. `run` is referentially stable, so wrapping calls
 * still keep useCallback identities stable for effect dependencies.
 *
 * @example
 * const [isLoading, whileLoading] = useBusy();
 * const getList = useCallback(
 *   (params) => whileLoading(() => get('/items', params)),
 *   [get, whileLoading],
 * );
 */
export function useBusy() {
    const [busy, setBusy] = useState(false);

    const run = useCallback(async <R,>(fn: () => Promise<R>): Promise<R> => {
        setBusy(true);
        try {
            return await fn();
        } finally {
            setBusy(false);
        }
    }, []);

    return [busy, run] as const;
}
